import { StrictMode } from 'react';
import type { Root } from 'react-dom/client';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { createBrowserRouter } from 'react-router';

import { App } from '@/app/app';
import { ConfigurationUnavailable } from '@/app/pages/configuration-unavailable';
import type { AppConfig } from '@/app/providers/config';
import { loadAppConfig } from '@/app/providers/config';
import type { ThemePreference } from '@/app/providers/theme';
import {
  applyTheme,
  DEFAULT_THEME_PREFERENCE,
  resolveTheme,
  storedThemePreference,
} from '@/app/providers/theme';
import { APP_ROUTES } from '@/app/routes';
import { loadInitialSession } from '@/features/auth/restore';
import { applyDocumentLocale, createI18n, initialLocale } from '@/i18n';

/**
 * Starting the application: configuration first, then one render.
 *
 * **Nothing is drawn before `GET /api/v1/config` has answered.** The SPA is served same-origin with
 * no build-time environment variables (Section 1.1), so the issuance mode, the reporting timezone
 * and the installation's default language are values this bundle learns at run time — and every one
 * of them changes what a screen looks like. Rendering first and correcting afterwards would mean an
 * operator in a pre-printed installation briefly seeing the on-demand registration form, and would
 * mean a date rendered in the browser's zone before the building's.
 *
 * **A failure is one actionable screen, not a degraded application.** `ConfigurationUnavailable`
 * says what is wrong and offers to try again; it renders in the language the terminal remembers,
 * falling back to the product default, because the installation's own default is one of the things
 * that could not be fetched.
 *
 * **The theme is applied to the document before the tree is mounted**, so the first paint is already
 * the right one. A theme applied in an effect paints light and then repaints dark, which is the
 * flash every operator notices and nobody can describe.
 */
export interface BootstrapOptions {
  readonly container: HTMLElement;
  readonly fetchImpl?: typeof globalThis.fetch;
  readonly storage?: Pick<Storage, 'getItem'>;
  readonly documentElement?: HTMLElement;
}

export async function startApplication(options: BootstrapOptions): Promise<Root> {
  const fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const storage = options.storage ?? globalThis.localStorage;
  const documentElement = options.documentElement ?? document.documentElement;

  const themePreference: ThemePreference =
    storedThemePreference(storage) ?? DEFAULT_THEME_PREFERENCE;
  applyTheme(resolveTheme(themePreference), documentElement);

  const root = createRoot(options.container);

  // Started before the configuration is awaited, because neither needs the other and a reload at a
  // gate should not cost two round trips in series before anything is on screen. It never rejects,
  // and it tells "nobody is signed in" apart from "that could not be asked": the first is the
  // sign-in screen and the second is not, because a staff member who is signed in must not be put
  // in front of a password form by a brief rate limit or an api restart.
  const restoring = loadInitialSession(fetchImpl);

  let config: AppConfig;
  try {
    config = await loadAppConfig(fetchImpl);
  } catch {
    const fallback = initialLocale(storage);
    applyDocumentLocale(fallback, documentElement);
    const i18n = createI18n(fallback);
    root.render(
      <StrictMode>
        <I18nextProvider i18n={i18n}>
          <ConfigurationUnavailable
            onRetry={() => {
              globalThis.location.reload();
            }}
          />
        </I18nextProvider>
      </StrictMode>,
    );
    return root;
  }

  const restored = await restoring;
  const locale = initialLocale(storage, config.defaultLocale);
  // Declared on the document before the tree is mounted, exactly as the theme is: `index.html`
  // ships one language and the terminal may remember the other, and a document that declares the
  // wrong one is read aloud in the wrong voice.
  applyDocumentLocale(locale, documentElement);
  const i18n = createI18n(locale);

  if (restored.kind === 'unavailable') {
    root.render(
      <StrictMode>
        <I18nextProvider i18n={i18n}>
          <ConfigurationUnavailable
            reason="session"
            retryAfterSeconds={restored.retryAfterSeconds}
            onRetry={() => {
              globalThis.location.reload();
            }}
          />
        </I18nextProvider>
      </StrictMode>,
    );
    return root;
  }

  root.render(
    <StrictMode>
      <App
        config={config}
        i18n={i18n}
        themePreference={themePreference}
        fetchImpl={fetchImpl}
        initialSession={restored.kind === 'session' ? restored.session : null}
        router={createBrowserRouter([...APP_ROUTES])}
      />
    </StrictMode>,
  );
  return root;
}
